import React, { useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LaunchIcon from '@mui/icons-material/Launch'
import LockIcon from '@mui/icons-material/Lock'
import { safeHtmlProps } from '../../libs/sanitizeHtml'

const DEFAULT_PRIMARY = '#03adec'

// Último segmento do link (o código público da página de pagamento), pra mostrar
// na barra de endereço do mini-navegador com destaque.
function linkCode(link) {
  const clean = String(link || '').split('?')[0].split('#')[0]
  const seg = clean.replace(/\/+$/, '').split('/').pop()
  return seg || ''
}

function hostFromLink(link) {
  const match = String(link || '').match(/^https?:\/\/([^/]+)/i)
  return match ? match[1] : 'pay.hipersend.com.br'
}

// Banner HTML do cliente, já sanitizado. Renderiza nada se vier vazio.
function Banner({ html }) {
  if (!html || !html.trim()) return null
  return (
    <div
      className="mb-3 text-left"
      style={{ color: '#1a2430', fontSize: 13, lineHeight: 1.4 }}
      {...safeHtmlProps(html)}
    />
  )
}

// Preview fiel da página de pagamento HiperPay, reconstruída a partir dos dados do
// payload (valor, pix, boleto) + branding do profile + banners ativos. NÃO carrega
// o link real — o único caminho pra ele é o botão "Abrir página real", por clique.
export function HiperPayPreview({ data, profile, banners, link }) {
  const [copied, setCopied] = useState(false)

  if (!data) return null

  const primary = profile?.primaryColor || DEFAULT_PRIMARY
  const code = linkCode(link)
  const host = hostFromLink(link)
  const topBanners = banners?.top || []
  const bottomBanners = banners?.bottom || []

  const headTitle = profile?.title || 'Fatura disponível para pagamento'
  const headSubtitle = profile?.subtitle || 'Pagamento via PIX confirma em minutos'

  return (
    <div
      className="w-full max-w-[400px] rounded-xl overflow-hidden"
      style={{
        background: '#fff',
        boxShadow: '0 6px 24px rgba(0,0,0,.18)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
        color: '#1a2430',
      }}
    >
      {/* Chrome do mini-navegador */}
      <div
        className="flex items-center gap-2 px-3 py-2"
        style={{ background: '#f1f3f4', borderBottom: '1px solid #e3e6e8' }}
      >
        <div className="flex gap-1.5">
          <span className="block w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
          <span className="block w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
          <span className="block w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
        </div>
        <div
          className="flex-1 flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] truncate"
          style={{ background: '#fff', border: '1px solid #e3e6e8', color: '#5b6b75' }}
        >
          <LockIcon style={{ fontSize: 12, color: '#0b8a66' }} />
          <span className="truncate">
            {host}/<b style={{ color: primary }}>{code}</b>
          </span>
        </div>
      </div>

      {/* Vitrine */}
      <div className="p-3.5" style={{ background: '#f6f7f9' }}>
        {/* Logo / marca */}
        <div className="text-center py-2.5">
          {profile?.logoUrl ? (
            <img
              src={profile.logoUrl}
              alt={profile?.name || 'logo'}
              style={{ width: profile?.logoWidth || '160px', maxWidth: '70%', display: 'inline-block' }}
            />
          ) : (
            <span className="font-extrabold text-lg" style={{ color: primary }}>
              {profile?.name || 'Página de pagamento'}
            </span>
          )}
        </div>

        {topBanners.map((b) => (
          <Banner key={b._id || b.title} html={b.htmlContent} />
        ))}

        {/* Card de pagamento */}
        <div className="rounded-xl overflow-hidden" style={{ background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
          <div className="px-4 py-3.5 text-white" style={{ background: primary }}>
            <div className="text-[13px] font-bold">{headTitle}</div>
            <div className="text-[11px]" style={{ opacity: 0.92 }}>{headSubtitle}</div>
            {data.price && <div className="text-[26px] font-extrabold mt-1.5">{data.price}</div>}
          </div>

          {data.pixCode && (
            <div className="px-4 py-3.5">
              <div className="text-[11px] font-bold mb-1.5">Código pix copia e cola</div>
              <div
                className="flex items-center gap-2 rounded-lg p-2.5 text-[10px] break-all"
                style={{ border: '1.5px solid ' + primary, background: '#f7fffb', color: '#3a4654' }}
              >
                <span className="flex-1">{data.pixCode}</span>
                <CopyToClipboard
                  text={data.pixCode}
                  onCopy={() => {
                    setCopied(true)
                    setTimeout(() => setCopied(false), 1500)
                  }}
                >
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[10px] font-bold text-white whitespace-nowrap"
                    style={{ background: primary }}
                  >
                    {copied ? <CheckIcon style={{ fontSize: 13 }} /> : <ContentCopyIcon style={{ fontSize: 13 }} />}
                    {copied ? 'COPIADO' : 'COPIAR'}
                  </button>
                </CopyToClipboard>
              </div>
            </div>
          )}

          {data.bankSlip && (
            <div className="px-4 pb-3.5 text-[10.5px]" style={{ color: '#5b6b75' }}>
              Ou pague por boleto: <span className="break-all">{data.bankSlip}</span>
            </div>
          )}
        </div>

        {bottomBanners.map((b) => (
          <Banner key={b._id || b.title} html={b.htmlContent} />
        ))}

        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-1 mt-3 text-[11px] font-bold hover:underline"
            style={{ color: '#0b8a66' }}
          >
            <LaunchIcon style={{ fontSize: 14 }} />
            Abrir página de pagamento real
          </a>
        )}

        <div className="text-center text-[10px] mt-2.5" style={{ color: '#94a3b8' }}>
          Pagamento processado com segurança · HiperPay
        </div>
      </div>
    </div>
  )
}

export default HiperPayPreview
